// All pricing page data in one file. To change prices, features, or FAQ — edit here only.
// Numeric plan limits (CU, AI credits, workflows, price) come from the backend API (/v1/tiers).
// Static values below are fallbacks shown before backend data loads.

// =============================================================================
// CONFIG — Edit this section to change everything on the pricing page
// =============================================================================

import type { TierInfo } from "@/types/billing";

export const YEARLY_DISCOUNT_PERCENT = 30;

// -- Support level labels (used for static fallback before backend loads) --

const STATIC_SUPPORT_LEVELS: Record<string, string> = {
  free: "community",
  starter: "email",
  pro: "priority",
  biz: "dedicated",
  enterprise: "dedicated",
};

const STATIC_MAX_USERS: Record<string, number> = {
  free: 1,
  starter: 1,
  pro: 10,
  biz: 0,
  enterprise: 0,
};

/** Format maxUsers: 0 means unlimited */
function formatUsers(maxUsers: number): string {
  if (maxUsers === 0) return "Unlimited users";
  return `${maxUsers} ${maxUsers === 1 ? "user" : "users"}`;
}

/** Capitalize first letter of support level */
function formatSupportLevel(level: string): string {
  return `${level.charAt(0).toUpperCase() + level.slice(1)} support`;
}

// -- Derived card features --
// Items containing "Compute Units" get an info tooltip in PricingCard.

/** Format a number with commas (e.g. 400000 → "400,000") */
export function formatNumber(n: number): string {
  return n.toLocaleString("en-US");
}

/** Static-only features shown before backend data loads. */
function buildStaticFeatures(id: string): string[] {
  const maxUsers = STATIC_MAX_USERS[id];
  const supportLevel = STATIC_SUPPORT_LEVELS[id];
  if (maxUsers === undefined || !supportLevel) return [];
  if (id === "enterprise") {
    return [
      "Unlimited active workflows",
      "Custom Compute Units allocation",
      "Unlimited users",
      "Custom AI credits",
      "Dedicated account manager",
      "Priority support",
      "Custom SLA",
    ];
  }
  return [formatUsers(maxUsers), "All triggers & actions", "Multi-step workflows", formatSupportLevel(supportLevel)];
}

/** Build full card features from backend TierInfo. */
export function buildCardFeaturesFromBackend(id: string, bt: TierInfo): string[] {
  if (id === "enterprise") return buildStaticFeatures(id);
  return [
    `${formatNumber(bt.maxActiveWorkflows)} active workflows`,
    `${formatNumber(bt.maxDatabaseTables)} database tables`,
    `${formatNumber(bt.cuAmount)} Compute Units / mo`,
    formatUsers(bt.maxUsers),
    `${formatNumber(bt.aiCredits)} AI credits`,
    "All triggers & actions",
    "Multi-step workflows",
    formatSupportLevel(bt.supportLevel),
  ];
}

// -- CTA buttons --
export const CTA_FREE = { text: "Get Free Plan", href: "/workflows" };
export const CTA_STARTER = { text: "Get Starter Plan", href: "/billing?upgrade=starter" };
export const CTA_PRO = { text: "Get Pro Plan", href: "/billing?upgrade=pro" };
export const CTA_BIZ = { text: "Get Business Plan", href: "mailto:contact@npclabs.org" };
export const CTA_ENTERPRISE = {
  text: "Contact Sales",
  href: "mailto:contact@npclabs.org?subject=Enterprise%20Plan%20Inquiry",
};

// -- FAQ --
export const FAQ_DATA: FAQItem[] = [
  {
    question: "What are Compute Units (CU)?",
    answer:
      "CU is the usage-based unit for workflow execution. Each action in a workflow consumes CU — simple actions like delays or filters cost as little as 2 CU, while on-chain transactions like token transfers can cost up to 500 CU. This ensures fair pricing based on actual resource usage.",
  },
  {
    question: "What are AI Credits?",
    answer:
      "AI Credits are a separate balance used when you interact with the AI agent to create or edit workflows. They are included in every plan alongside CU and reset each billing cycle.",
  },
  {
    question: "What happens when I run out of CU or AI Credits?",
    answer:
      "When your CU balance reaches zero, your active workflows are paused automatically. You can upgrade to a higher plan for more CU, or wait until your next billing cycle when your balance resets. AI Credits work the same way — once depleted, AI-assisted workflow creation is unavailable until the next reset.",
  },
  {
    question: "Do unused CU or AI Credits roll over?",
    answer:
      "No. Both CU and AI Credits reset at the start of each billing cycle. For free users, this is the 1st of each month. For paid users, it's your billing anchor date — the day you first subscribed.",
  },
  {
    question: "Can I upgrade or downgrade my plan?",
    answer:
      "Yes. Upgrades take effect immediately — you are charged a prorated amount for the rest of your billing cycle, and the new tier's full CU and AI Credits are added on top of your existing balance. Downgrades between paid plans take effect at the end of your current billing period — your workflow limits adjust immediately, but you keep your current tier's CU and AI Credits until the period ends. Downgrading to Free also takes effect at the end of your current billing period.",
  },
  {
    question: "What happens when I cancel my subscription?",
    answer:
      "Cancellation takes effect at the end of your current billing period — you keep your paid benefits until then. Once the period ends, your organization is downgraded to the Free tier and your balances are reset to Free tier amounts.",
  },
  {
    question: "What payment methods do you accept?",
    answer:
      "We accept credit cards via Stripe and cryptocurrency payments via AnySpend (cross-chain). Both payment methods provide the same tier benefits.",
  },
  {
    question: "What happens if my payment fails?",
    answer:
      "Stripe automatically retries failed charges multiple times over a period of up to 3 weeks and sends you email notifications to update your payment method. If all retries are exhausted, your subscription is canceled and your organization is downgraded to the Free tier.",
  },
];

// =============================================================================
// BELOW THIS LINE — Types, computed data, helpers. No need to edit.
// =============================================================================

// -- Types --

export interface PricingTier {
  id: string;
  name: string;
  description: string;
  popular: boolean;
  ctaText: string;
  ctaHref: string;
  features: string[];
  includedFeatures: string[];
}

export interface ComparisonRow {
  feature: string;
  free: boolean | string;
  starter: boolean | string;
  pro: boolean | string;
  biz: boolean | string;
  enterprise: boolean | string;
}

export interface ComparisonGroup {
  category: string;
  rows: ComparisonRow[];
}

export interface FAQItem {
  question: string;
  answer: string;
}

export const TIER_ICON_MAP: Record<string, string> = {
  free: "/images/pricing/icons/tier-free.png",
  starter: "/images/pricing/icons/tier-starter.png",
  pro: "/images/pricing/icons/tier-pro.png",
  biz: "/images/pricing/icons/tier-business.png",
  enterprise: "/images/pricing/icons/tier-enterprise.png",
};

// -- Tier data (built from config above) --

export const PRICING_TIERS: PricingTier[] = [
  {
    id: "free",
    name: "Free",
    description: "Free for everyone.",
    popular: false,
    ctaText: CTA_FREE.text,
    ctaHref: CTA_FREE.href,
    features: buildStaticFeatures("free"),
    includedFeatures: ["All triggers & actions", "Multi-step workflows", "Community support"],
  },
  {
    id: "starter",
    name: "Starter",
    description: "For individuals getting started with workflow automation.",
    popular: true,
    ctaText: CTA_STARTER.text,
    ctaHref: CTA_STARTER.href,
    features: buildStaticFeatures("starter"),
    includedFeatures: ["All triggers & actions", "Multi-step workflows", "Email support"],
  },
  {
    id: "pro",
    name: "Pro",
    description: "For growing teams that need power and speed.",
    popular: false,
    ctaText: CTA_PRO.text,
    ctaHref: CTA_PRO.href,
    features: buildStaticFeatures("pro"),
    includedFeatures: ["All triggers & actions", "Multi-step workflows", "Priority support"],
  },
  {
    id: "biz",
    name: "Business",
    description: "For teams and businesses that need power and speed.",
    popular: false,
    ctaText: CTA_BIZ.text,
    ctaHref: CTA_BIZ.href,
    features: buildStaticFeatures("biz"),
    includedFeatures: ["All triggers & actions", "Multi-step workflows", "Dedicated support"],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    description: "For organizations with advanced automation needs",
    popular: false,
    ctaText: CTA_ENTERPRISE.text,
    ctaHref: CTA_ENTERPRISE.href,
    features: buildStaticFeatures("enterprise"),
    includedFeatures: [],
  },
];

// -- Comparison table data --

const SUPPORT_TIERS = ["community", "email", "priority", "dedicated"] as const;
const SUPPORT_TIER_LABELS: Record<string, string> = {
  community: "Community",
  email: "Email",
  priority: "Priority",
  dedicated: "Dedicated",
};
function supportIncludes(plan: string, level: (typeof SUPPORT_TIERS)[number], backendTiers?: TierInfo[]): boolean {
  let planSupportLevel: string;
  if (backendTiers) {
    const bt = backendTiers.find(t => t.name.toLowerCase() === plan);
    planSupportLevel = bt?.supportLevel ?? "community";
  } else {
    planSupportLevel = STATIC_SUPPORT_LEVELS[plan] ?? "community";
  }
  const planIdx = SUPPORT_TIERS.indexOf(planSupportLevel as (typeof SUPPORT_TIERS)[number]);
  const targetIdx = SUPPORT_TIERS.indexOf(level);
  return planIdx >= targetIdx;
}

function supportRow(level: (typeof SUPPORT_TIERS)[number], backendTiers?: TierInfo[]): ComparisonRow {
  return {
    feature: SUPPORT_TIER_LABELS[level] ?? level,
    free: supportIncludes("free", level, backendTiers),
    starter: supportIncludes("starter", level, backendTiers),
    pro: supportIncludes("pro", level, backendTiers),
    biz: supportIncludes("biz", level, backendTiers),
    enterprise: supportIncludes("enterprise", level, backendTiers),
  };
}

/** Static comparison rows (fields NOT from backend) */
export const STATIC_COMPARISON_DATA: ComparisonGroup[] = [
  {
    category: "Triggers",
    rows: [{ feature: "All triggers", free: true, starter: true, pro: true, biz: true, enterprise: true }],
  },
  {
    category: "Support",
    rows: SUPPORT_TIERS.map(level => supportRow(level)),
  },
];

/** Build resource comparison rows from backend tier data */
export function buildResourceComparisonRows(backendTiers: TierInfo[]): ComparisonGroup {
  const tierMap = new Map(backendTiers.map(t => [t.name.toLowerCase(), t]));

  function resourceValue(tierId: string, getter: (t: TierInfo) => number): string {
    if (tierId === "enterprise") return "Custom";
    const t = tierMap.get(tierId);
    if (!t) return "—";
    return formatNumber(getter(t));
  }

  function resourceRow(feature: string, getter: (t: TierInfo) => number): ComparisonRow {
    return {
      feature,
      free: resourceValue("free", getter),
      starter: resourceValue("starter", getter),
      pro: resourceValue("pro", getter),
      biz: resourceValue("biz", getter),
      enterprise: resourceValue("enterprise", getter),
    };
  }

  function usersValue(tierId: string, map: Map<string, TierInfo>): string {
    if (tierId === "enterprise") return "Custom";
    const t = map.get(tierId);
    if (!t) return "—";
    return t.maxUsers === 0 ? "Unlimited" : String(t.maxUsers);
  }

  return {
    category: "Resources",
    rows: [
      resourceRow("Compute Units / month", t => t.cuAmount),
      resourceRow("AI Credits / month", t => t.aiCredits),
      resourceRow("Active workflows", t => t.maxActiveWorkflows),
      resourceRow("Database tables", t => t.maxDatabaseTables),
      {
        feature: "Users",
        free: usersValue("free", tierMap),
        starter: usersValue("starter", tierMap),
        pro: usersValue("pro", tierMap),
        biz: usersValue("biz", tierMap),
        enterprise: usersValue("enterprise", tierMap),
      },
    ],
  };
}
